import { SmartSendItemApiModel } from '~/lib/api/smartsend/types';
import { ItemRecipientMobile } from './ItemRecipient';
import { P2GBox, P2GButton, P2GCheckBoxLegacy, P2GDate, P2GLink, P2GText } from '@parcel2go/core-ui';
import { ItemContentsMobile } from './ItemContents';
import { ServiceImage } from './ItemService';

import { EditType } from './EditType';
import { GetExternalTrackingNumber } from '~/apps/smartsend/lib/helpers/SmartSendSummaryGenerator';
import { SettingsCache } from '~/lib/consts/settings-cache';
import LiveTrackingProgress from './LiveTrackingProgress';
import ItemCollectionStatus from './ItemCollectionStatus';
import { ItemMarketplaceDetailsMobile } from './ItemMarketplaceDetailsMobile';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';
import { Suspense } from 'react';
import PrintInStoreQrCodesAutoLoad from '../edit/PrintInStoreQrCodesAutoLoad';
import { PrintInStoreExtra } from '~/lib/consts/extras';

interface props {
  item: SmartSendItemApiModel;
  toggleSelect: (val: boolean) => void;
  edit: (type: EditType) => void;
  isReturn: boolean;
}

export default function SmartSendOrderProcessOrderMobile(props: props) {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const edit = () => {
    props.edit && props.edit('ORDER-ACTIONS');
  };

  const details = () => {
    props.edit && props.edit('ORDER-DETAILS');
  };

  const labels = () => {
    props.edit && props.edit('ORDER-LABELS');
  };

  const trackingNumber = GetExternalTrackingNumber(props.item);
  const inTransit = !!(props.item.TrackingSummary?.Collected || props.item.TrackingSummary?.DroppedOff);
  const isPrintInStore = props.item.Extras.some((e) => e.Id === PrintInStoreExtra);

  return (
    <P2GBox p={1}>
      <P2GBox p={1} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
        <P2GText variant="subtitle2" component="div" flexGrow={1}>
          {props.item.BookingDetails?.Reference}
          {trackingNumber && (
            <P2GText component="div" variant="caption">
              {trackingNumber}
            </P2GText>
          )}
        </P2GText>
        <P2GCheckBoxLegacy
          labelPlacement="start"
          id="Chk"
          onChange={props.toggleSelect}
          checked={props.item.State.selected}
          label={props.item.BookingDetails?.Service && <ServiceImage width={60} service={props.item.Service} />}
          variant="primary"
          error={false}
        />
      </P2GBox>
      <ItemCollectionStatus wrapper={(x) => <P2GBox px={1}>{x}</P2GBox>} variant="body2" item={props.item} />
      {SettingsCache.settings.Features.includes('Tracking') && (
        <LiveTrackingProgress
          item={props.item}
          wrapper={(x) => (
            <P2GBox p={1}>
              {x}
              <P2GBox textAlign="center" sx={{ display: 'flex', mt: 1, justifyContent: 'space-between' }}>
                {props.item?.BookingDetails?.Created && (
                  <P2GText variant="body2">
                    <strong>Booked:</strong>{' '}
                    <P2GDate variant="body2" date={new Date(props.item.BookingDetails.Created)} />
                  </P2GText>
                )}
                <P2GLink onClick={() => props.edit('SMARTSEND-ORDER-TRACKING')} variant="body2">
                  {t('TRACKING_DETAILS_LINK', { defaultValue: 'Tracking Details' })}
                </P2GLink>
              </P2GBox>
            </P2GBox>
          )}
        />
      )}
      {!inTransit && isPrintInStore && (
        <Suspense>
          <PrintInStoreQrCodesAutoLoad item={props.item} wrapper={(x) => <P2GBox p={1}>{x}</P2GBox>} />
        </Suspense>
      )}
      <P2GBox p={1} backgroundColour="#f1f1f1">
        <ItemRecipientMobile
          edit={edit}
          customer={props.isReturn ? props.item.Sender : props.item.Recipient}
          revealText={t('VIEW_LINK', 'View')}
          variant="body2"
        />
      </P2GBox>
      <P2GBox p={1}>
        <ItemContentsMobile edit={edit} item={props.item} revealText={t('VIEW_LINK', 'View')} variant="body2" />
      </P2GBox>
      <ItemMarketplaceDetailsMobile item={props.item} removeTag={() => {}} saveTags={() => {}} />
      <P2GBox p={1} sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
        <P2GButton variant="tertiary" fullWidth onClick={edit}>
          {t('VIEW_ACTIONS_LINK', { defaultValue: 'Actions' })}
        </P2GButton>
        {inTransit || isPrintInStore ? (
          <P2GButton variant="secondary" fullWidth onClick={details}>
            {t('DETAILS_LINK', { defaultValue: 'Details' })}
          </P2GButton>
        ) : (
          <P2GButton variant="secondary" fullWidth onClick={labels}>
            {t('LABELS_LINK', { defaultValue: 'Labels' })}
          </P2GButton>
        )}
      </P2GBox>
    </P2GBox>
  );
}
